import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';

export default function Terms() {
  const location = useLocation();

  // Smooth scroll to section if hash is present
  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.slice(1));
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  const sections = [
    { id: 'overview', title: '1. Overview' },
    { id: 'user-accounts', title: '2. User Accounts' },
    { id: 'use-of-platform', title: '3. Use of the Platform' },
    { id: 'tickets-purchases', title: '4. Tickets & Purchases' },
    { id: 'refunds', title: '5. Refunds' },
    { id: 'organizer-responsibilities', title: '6. Organizer Responsibilities' },
    { id: 'payments-payouts', title: '7. Payments & Payouts' },
    { id: 'intellectual-property', title: '8. Intellectual Property' },
    { id: 'limitation-of-liability', title: '9. Limitation of Liability' },
    { id: 'termination', title: '10. Termination' },
    { id: 'changes-to-terms', title: '11. Changes to Terms' },
    { id: 'contact-us', title: '12. Contact Us' },
  ];

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-12 md:py-16">
        {/* Header */}
        <div className="mb-12">
          <h1 className="text-3xl md:text-4xl font-bold mb-4">Terms of Service</h1>
          <p className="text-muted-foreground">Last Updated: 1st April, 2026</p>
        </div>

        {/* Table of Contents */}
        <nav className="mb-12 p-6 bg-muted/30 rounded-lg border border-border">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-4">
            Table of Contents
          </h2>
          <ul className="grid sm:grid-cols-2 gap-2">
            {sections.map((section) => (
              <li key={section.id}>
                <a
                  href={`#${section.id}`}
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById(section.id)?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  {section.title}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        {/* Introduction */}
        <div className="mb-10">
          <p className="text-muted-foreground leading-relaxed">
            Welcome to NextGig. By accessing or using our platform, you agree to the following Terms of Service. Please read them carefully.
          </p>
        </div>

        {/* Content */}
        <div className="space-y-10">
          
          {/* Section 1 */}
          <section id="overview">
            <h2 className="text-xl md:text-2xl font-bold mb-4">1. Overview</h2>
            <p className="text-muted-foreground leading-relaxed">
              NextGig is an event ticketing and promotion platform that allows users to discover events and enables organizers to create, promote, and sell tickets.
            </p>
          </section>

          {/* Section 2 */}
          <section id="user-accounts">
            <h2 className="text-xl md:text-2xl font-bold mb-4">2. User Accounts</h2>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
              <li>You may be required to create an account to access certain features.</li>
              <li>You are responsible for maintaining the confidentiality of your account information.</li>
              <li>You agree to provide accurate and up-to-date information.</li>
            </ul>
          </section>

          {/* Section 3 */}
          <section id="use-of-platform">
            <h2 className="text-xl md:text-2xl font-bold mb-4">3. Use of the Platform</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">
              You agree to use NextGig only for lawful purposes. You may not:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
              <li>Use the platform for fraudulent or misleading activities</li>
              <li>Interfere with the operation or security of the platform</li>
              <li>Violate any applicable laws or regulations</li>
            </ul>
          </section>

          {/* Section 4 */}
          <section id="tickets-purchases">
            <h2 className="text-xl md:text-2xl font-bold mb-4">4. Tickets & Purchases</h2>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
              <li>All ticket purchases are subject to event availability.</li>
              <li>Prices and event details are set by event organizers.</li>
              <li>NextGig is not responsible for changes, cancellations, or issues related to events hosted by third parties.</li>
            </ul>
          </section>

          {/* Section 5 */}
          <section id="refunds">
            <h2 className="text-xl md:text-2xl font-bold mb-4">5. Refunds</h2>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
              <li>Refunds are handled according to NextGig's refund policy and/or the event organizer's terms.</li>
              <li>Users should review event-specific refund policies before purchasing tickets.</li>
            </ul>
          </section>

          {/* Section 6 */}
          <section id="organizer-responsibilities">
            <h2 className="text-xl md:text-2xl font-bold mb-4">6. Organizer Responsibilities</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">
              Event organizers agree to:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
              <li>Provide accurate event information</li>
              <li>Honor all ticket sales</li>
              <li>Comply with applicable laws and regulations</li>
              <li>Deliver the event as described</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4">
              NextGig reserves the right to suspend or remove organizers who violate these terms.
            </p>
          </section>

          {/* Section 7 */}
          <section id="payments-payouts">
            <h2 className="text-xl md:text-2xl font-bold mb-4">7. Payments & Payouts</h2>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
              <li>Payments are processed through secure third-party providers.</li>
              <li>Organizers will receive payouts according to NextGig's payout schedule.</li>
              <li>NextGig is not responsible for delays caused by payment providers or external factors.</li>
            </ul>
          </section>

          {/* Section 8 */}
          <section id="intellectual-property">
            <h2 className="text-xl md:text-2xl font-bold mb-4">8. Intellectual Property</h2>
            <p className="text-muted-foreground leading-relaxed">
              All content on NextGig, including logos, design, and software, is owned by, or licensed to NextGig and may not be used without permission.
            </p>
          </section>

          {/* Section 9 */}
          <section id="limitation-of-liability">
            <h2 className="text-xl md:text-2xl font-bold mb-4">9. Limitation of Liability</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">
              NextGig is not liable for:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
              <li>Any losses related to event cancellations, changes, or disputes</li>
              <li>Indirect or consequential damages arising from use of the platform</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4">
              Use of the platform is at your own risk.
            </p>
          </section>

          {/* Section 10 */}
          <section id="termination">
            <h2 className="text-xl md:text-2xl font-bold mb-4">10. Termination</h2>
            <p className="text-muted-foreground leading-relaxed">
              We reserve the right to suspend or terminate accounts that violate these Terms of Service.
            </p>
          </section>

          {/* Section 11 */}
          <section id="changes-to-terms">
            <h2 className="text-xl md:text-2xl font-bold mb-4">11. Changes to Terms</h2>
            <p className="text-muted-foreground leading-relaxed">
              NextGig may update these Terms at any time. Continued use of the platform constitutes acceptance of any changes.
            </p>
          </section>

          {/* Section 12 */}
          <section id="contact-us">
            <h2 className="text-xl md:text-2xl font-bold mb-4">12. Contact Us</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              If you have any questions about these Terms, please contact us at:
            </p>
            <div className="bg-muted/50 rounded-lg p-6">
              <a 
                href="mailto:Nextgigsuite@gmail.com" 
                className="text-primary hover:underline font-medium"
              >
                Nextgigsuite@gmail.com
              </a>
            </div>
          </section>

        </div>
      </div>
    </Layout>
  );
}
